import { PatientLayout } from "@/components/layout/patient-layout";
import { useListResources } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Input } from "@/components/ui/input";
import {
  ExternalLink,
  Search,
  Users,
  BookOpen,
  Leaf,
  Activity,
  Building2,
  LayoutGrid,
  PhoneCall,
} from "lucide-react";
import { useMemo } from "react";
import { useSearchParam } from "@/hooks/use-search-param";
import { Highlight } from "@/components/ui/highlight";

// ---------------------------------------------------------------------------
// Category config
// ---------------------------------------------------------------------------
const CATEGORY_MAP: Record<string, string> = {
  Support: "Support",
  Conditions: "Information",
  Lifestyle: "Lifestyle",
  "Symptom Management": "Symptom Help",
  Professional: "Organisations",
  "Professional Standards": "Organisations",
  Guidelines: "Organisations",
  "Clinical Reference": "Organisations",
};

const PATIENT_TABS: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: "", label: "All", icon: <LayoutGrid className="h-3.5 w-3.5" /> },
  { key: "Support", label: "Support", icon: <Users className="h-3.5 w-3.5" /> },
  { key: "Information", label: "Information", icon: <BookOpen className="h-3.5 w-3.5" /> },
  { key: "Lifestyle", label: "Lifestyle", icon: <Leaf className="h-3.5 w-3.5" /> },
  { key: "Symptom Help", label: "Symptom Help", icon: <Activity className="h-3.5 w-3.5" /> },
  { key: "Organisations", label: "Organisations", icon: <Building2 className="h-3.5 w-3.5" /> },
];

const CATEGORY_COLORS: Record<string, string> = {
  Support: "bg-rose-50 text-rose-700 border-rose-200",
  Information: "bg-blue-50 text-blue-700 border-blue-200",
  Lifestyle: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Symptom Help": "bg-amber-50 text-amber-700 border-amber-200",
  Organisations: "bg-slate-50 text-slate-600 border-slate-200",
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Support: <PhoneCall className="h-4 w-4" />,
  Information: <BookOpen className="h-4 w-4" />,
  Lifestyle: <Leaf className="h-4 w-4" />,
  "Symptom Help": <Activity className="h-4 w-4" />,
  Organisations: <Building2 className="h-4 w-4" />,
};

function mapCategory(raw?: string | null): string {
  if (!raw) return "Organisations";
  return CATEGORY_MAP[raw] ?? "Organisations";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function PatientResources() {
  const [search, setSearch] = useSearchParam("q", "");
  const [activeTab, setActiveTab] = useSearchParam("cat", "");
  const { data: resources, isLoading } = useListResources({});

  const patientResources = useMemo(
    () => (resources ?? []).filter(r => r.audience === "patient" || r.audience === "both"),
    [resources]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return patientResources.filter(r => {
      const mappedCat = mapCategory(r.category);
      const matchesTab = !activeTab || mappedCat === activeTab;
      const matchesSearch =
        !search ||
        r.title.toLowerCase().includes(q) ||
        r.organisation.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q);
      return matchesTab && matchesSearch;
    });
  }, [patientResources, search, activeTab]);

  const countByTab = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of patientResources) {
      const cat = mapCategory(r.category);
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    return counts;
  }, [patientResources]);

  if (isLoading) return <PatientLayout title="Trusted Resources"><LoadingScreen /></PatientLayout>;

  return (
    <PatientLayout title="Trusted Resources">
      {/* Header */}
      <div className="mb-8 max-w-3xl">
        <p className="text-lg text-slate-600 leading-relaxed">
          Links from trusted UK organisations — charities, NHS resources, helplines, and support groups to help you manage your condition with confidence.
        </p>
      </div>

      {/* Search */}
      <div className="mb-5 max-w-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search resources…"
            className="pl-9"
          />
        </div>
      </div>

      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {PATIENT_TABS.map(tab => {
          const count = tab.key ? (countByTab[tab.key] ?? 0) : patientResources.length;
          if (tab.key && count === 0) return null;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                isActive
                  ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-amber-300 hover:text-amber-700"
              }`}
            >
              {tab.icon}
              {tab.label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 ${isActive ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Results count */}
      <p className={`mb-5 text-sm ${filtered.length === 0 ? "text-rose-500" : "text-slate-500"}`}>
        {filtered.length === 0
          ? "No resources found"
          : `Showing ${filtered.length} ${filtered.length === 1 ? "resource" : "resources"}${activeTab ? ` in ${activeTab}` : ""}${search ? ` for "${search}"` : ""}`}
      </p>

      {/* Cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map(r => {
          const mappedCat = mapCategory(r.category);
          const catColor = CATEGORY_COLORS[mappedCat] ?? CATEGORY_COLORS.Organisations;
          const catIcon = CATEGORY_ICONS[mappedCat];

          return (
            <a
              key={r.id}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
            >
              <div className="h-full bg-white border border-slate-200 rounded-2xl p-6 hover:border-amber-300 hover:shadow-md transition-all flex flex-col">
                {/* Category badge + external icon */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${catColor}`}>
                    {catIcon}
                    {mappedCat}
                  </span>
                  <ExternalLink className="h-4 w-4 text-slate-300 group-hover:text-amber-500 transition-colors shrink-0" />
                </div>

                {/* Title */}
                <h2 className="text-base font-bold text-slate-900 mb-1 group-hover:text-amber-700 transition-colors leading-snug">
                  <Highlight text={r.title} query={search} className="text-amber-700" />
                </h2>

                {/* Organisation */}
                <p className="text-sm font-medium text-amber-600 mb-3">
                  <Highlight text={r.organisation} query={search} className="text-amber-800" />
                </p>

                {/* Description */}
                {r.description && (
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 flex-1">
                    <Highlight text={r.description} query={search} className="text-amber-700" />
                  </p>
                )}

                <div className="mt-4 pt-3 border-t border-slate-100 text-xs font-medium text-amber-600 group-hover:text-amber-800 transition-colors flex items-center gap-1">
                  Visit website
                  <ExternalLink className="h-3 w-3" />
                </div>
              </div>
            </a>
          );
        })}

        {filtered.length === 0 && (
          <div className="col-span-full py-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
            {search || activeTab
              ? "No resources found matching your filters. Try clearing the search or selecting a different category."
              : "No resources available at the moment."}
          </div>
        )}
      </div>

      {/* Safety footer */}
      <div className="mt-10 p-5 bg-amber-50 rounded-2xl border border-amber-100">
        <p className="text-sm text-amber-800 leading-relaxed">
          <strong>Always check:</strong> These resources are from well-known UK organisations, but cannot replace advice from your own healthcare team. If you're worried about your condition or symptoms, contact your rheumatology nurse or GP, call <strong>NHS 111</strong>, or in an emergency call <strong>999</strong>.
        </p>
      </div>
    </PatientLayout>
  );
}
